import { ErrorState } from '@shared/components/feedback/ErrorState'
import { Button } from '@shared/components/ui/button'
import { formatZonedDateTime } from '@shared/lib/datetime/time-zone'
import { cn } from '@shared/lib/utils/cn'
import {
  AlertTriangle,
  AtSign,
  Bell,
  Check,
  CheckCheck,
  Megaphone,
  Settings2,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import {
  useDeleteNotification,
  useGetNotifications,
  useMarkAllAsRead,
  useMarkAsRead,
} from '../../hooks/useNotifications'
import type { Notification } from '../../types/notification.types'
import { NotificationType } from '../../types/notification.types'

const notificationIcons = {
  [NotificationType.COMMUNICATION_PUBLISHED]: Megaphone,
  [NotificationType.COMMUNICATION_UPDATED]: Bell,
  [NotificationType.COMMUNICATION_URGENT]: AlertTriangle,
  [NotificationType.DEPARTMENT_ASSIGNED]: Users,
  [NotificationType.MENTION]: AtSign,
  [NotificationType.SYSTEM]: Settings2,
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
}: {
  notification: Notification
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
}) {
  const Icon = notificationIcons[notification.type] || Bell
  const content = (
    <>
      <p className="text-sm font-medium text-foreground">{notification.title}</p>
      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{notification.message}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {formatZonedDateTime(notification.createdAt)}
      </p>
    </>
  )

  return (
    <div
      className={cn(
        'flex items-start gap-3 border-b border-border/70 p-4 transition-colors hover:bg-accent/40',
        !notification.isRead && 'bg-primary/5',
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        {notification.link ? (
          <Link
            to={notification.link}
            className="block"
            onClick={() => !notification.isRead && onMarkAsRead(notification.id)}
          >
            {content}
          </Link>
        ) : (
          <div className="block">{content}</div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {!notification.isRead && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMarkAsRead(notification.id)}
            className="h-7 w-7 p-0"
            title="Marcar como lida"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(notification.id)}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          title="Excluir"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

export function NotificationsDropdown() {
  const buttonId = useId()
  const panelId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const previousPath = useRef(location.pathname)
  useEffect(() => {
    if (previousPath.current !== location.pathname) {
      setIsOpen(false)
      previousPath.current = location.pathname
    }
  }, [location.pathname])
  const { data, isLoading, isError, refetch } = useGetNotifications({ limit: 10 })
  const markAsReadMutation = useMarkAsRead()
  const markAllAsReadMutation = useMarkAllAsRead()
  const deleteMutation = useDeleteNotification()

  const notifications = data?.data || []
  const unreadCount = data?.unreadCount || 0

  const handleMarkAsRead = (id: string) => {
    markAsReadMutation.mutate([id])
  }

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate()
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
  }

  useEffect(() => {
    if (!isOpen) return

    closeButtonRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  return (
    <div className="relative">
      <Button
        id={buttonId}
        variant="ghost"
        size="icon"
        className="relative rounded-full border border-transparent hover:border-border/80 hover:bg-accent/70"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-haspopup="dialog"
        aria-label={
          unreadCount > 0 ? `${unreadCount} notificações não lidas` : 'Abrir notificações'
        }
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setIsOpen(false)
              }
            }}
            aria-label="Fechar notificações"
          />

          <div
            id={panelId}
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby={`${panelId}-title`}
            className="fixed left-4 right-4 top-20 z-50 w-auto md:absolute md:left-auto md:right-0 md:top-12 md:w-[calc(100vw-2rem)] max-w-96 overflow-hidden rounded-[1.5rem] border border-border/70 bg-background/95 shadow-2xl backdrop-blur-xl"
          >
            <div className="flex items-center justify-between border-b border-border/70 p-4">
              <h3 id={`${panelId}-title`} className="font-semibold text-foreground">
                Notificações
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAllAsRead}
                    className="h-7 text-xs"
                    disabled={markAllAsReadMutation.isPending}
                  >
                    <CheckCheck className="mr-1 h-3.5 w-3.5" />
                    Marcar todas
                  </Button>
                )}
                <Button
                  ref={closeButtonRef}
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-7 w-7"
                  aria-label="Fechar notificações"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="max-h-[26rem] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                </div>
              ) : isError ? (
                <ErrorState message="Falha ao carregar notificações." retry={() => refetch()} />
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <Bell className="mb-2 h-12 w-12 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nenhuma notificação</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>

            {
              <div className="border-t border-border/70 p-3">
                <Link
                  to="/notifications"
                  className="block text-center text-sm text-primary hover:text-primary/80"
                  onClick={() => setIsOpen(false)}
                >
                  Ver todas as notificações
                </Link>
              </div>
            }
          </div>
        </>
      )}
    </div>
  )
}

NotificationsDropdown.displayName = 'NotificationsDropdown'
